import { useEffect, useRef } from "react";
import type { TextElementDto } from "@repo/api/overlays";
import { getLoadedFontFamily } from "@repo/video-engine-i2v/utils/font";
import { getTextLayoutStyle } from "@repo/video-engine-i2v/utils/text-layout";
import { useCanvasStore } from "@/lib/stores/canvas-store/canvas-store";
import { Box, Resolution } from "./canvas-geometry";

type Props = {
  element: TextElementDto;
  box: Box;
  resolution: Resolution;
  scale: number;
};

// The text stays rendered by the preview; this field has transparent text laid out the
// same way, so it only adds the caret and the text selection on top.
export default function TextEditor({ element, box, resolution, scale }: Props) {
  const ref = useRef<HTMLSpanElement>(null);
  const canvasService = useCanvasStore((state) => state.service);

  const { fontExternalId, fontWeight, fontStyle } = element.typography;
  const fontFamily = getLoadedFontFamily(fontExternalId, fontWeight, fontStyle);

  useEffect(() => {
    const field = ref.current;

    if (!field) {
      return;
    }

    field.textContent = element.text;
    field.focus();
    window.getSelection()?.selectAllChildren(field);
    // Only when editing starts; the field owns the text after that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!fontFamily) {
    return null;
  }

  return (
    <span
      ref={ref}
      data-keep-selection
      contentEditable="plaintext-only"
      suppressContentEditableWarning
      spellCheck={false}
      className="pointer-events-auto absolute origin-top-left cursor-text text-transparent caret-black outline-none selection:bg-tertiary-400/30"
      style={{
        ...getTextLayoutStyle(element.typography, fontFamily),
        left: `${(box.x / resolution.width) * 100}%`,
        top: `${(box.y / resolution.height) * 100}%`,
        transform: `scale(${scale})`,
      }}
      onInput={(event) =>
        canvasService.editText(event.currentTarget.textContent ?? "")
      }
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === "Escape") {
          event.preventDefault();
          canvasService.stopTextEditing();
        }
      }}
    />
  );
}
